import { BACKEND_URL } from "@/lib/api";
import { PageHeader } from "@/components/common/PageParts";
import { TtsTerminalPanel } from "@/components/tts/TtsTerminalPanel";
import { DevNav } from "@/components/developer/DevNav";
import { Link } from "react-router-dom";

const PORTAL = [["/developers/apps", "Applications", "Register apps, issue API keys and client secrets, manage collaborators and ownership."],
  ["/developers/docs", "Documentation", "Guides for API keys, OAuth2 + PKCE, pagination, errors and rate limits, plus the live endpoint reference."],
  ["/developers/explorer", "API explorer", "Try any public endpoint safely with a 15-minute in-memory token."],
  ["/developers/usage", "Usage", "Requests, errors and rate-limit use recorded from real traffic."]];

const ENDPOINTS = [
  ["POST", "/auth/login", "Sign in (cookie or returns session for Bearer use)"],
  ["POST", "/uploads/images?kind=card_image", "Upload up to 50 PNG/JPG/WebP images"],
  ["POST", "/uploads/metadata", "Parse CSV/JSON card metadata"],
  ["POST", "/cards", "Create a custom card"],
  ["POST", "/cards/bulk", "Create up to 500 cards (partial success, HTTP 207)"],
  ["GET", "/cards?q=&colors=&subtype=&mechanic=", "Search public custom cards"],
  ["GET", "/cards/{card_id}/versions", "Card version history"],
  ["GET", "/sets · /card-backs", "Browse sets and card backs"],
  ["GET", "/official/cards/search?q=", "Search official cards (Scryfall)"],
  ["POST", "/decks · PATCH /decks/{deck_id}", "Create and edit decks"],
  ["GET", "/decks/{deck_id}", "Deck with resolved cards, stats and format checks"],
  ["GET", "/decks/{deck_id}/tts?importer=1.0.0", "TTS manifest v3: spawn-ready objects per zone, uniform backs"],
  ["GET", "/tts/importer/version?installed=", "Deck Import Terminal release, minimum version, notes"],
  ["GET", "/tts/importer/saved-object.json · terminal.lua", "Download the Deck Import Terminal"],
  ["GET · PUT", "/tts/settings", "Site default TTS card back (owner/admin to change)"],
];

export default function DeveloperApi() {
  return (
    <div className="container-x py-12">
      <DevNav />
      <div className="mb-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="dev-portal-overview">
        {PORTAL.map(([to, t, d]) => <Link key={to} to={to} className="panel p-5 transition-colors hover:border-amber-500/60" data-testid={`dev-overview-${t.toLowerCase().replace(/\s/g, "-")}`}>
          <p className="font-heading text-lg text-amber-100">{t}</p><p className="mt-1 text-sm text-slate-400">{d}</p></Link>)}
      </div>
      <PageHeader eyebrow="Versioned REST · /api/v1" title="Developer API" testid="developers-header"
        description="The same API powers this site. Card-creation tools can upload images and structured metadata directly."
        actions={<>
          <a href={`${BACKEND_URL}/api/docs`} target="_blank" rel="noreferrer" className="btn-gold" data-testid="developers-docs-link">Interactive OpenAPI docs</a>
          <a href={`${BACKEND_URL}/api/openapi.json`} target="_blank" rel="noreferrer" className="btn-outline" data-testid="developers-openapi-link">openapi.json</a>
        </>} />
      <div className="grid gap-8 lg:grid-cols-12">
        <div className="panel overflow-x-auto lg:col-span-7">
          <table className="w-full text-left text-sm" data-testid="developers-endpoints-table">
            <thead className="bg-[#16213A] font-mono text-[10px] uppercase tracking-widest text-amber-300"><tr><th className="px-4 py-3">Method</th><th className="px-4 py-3">Path</th><th className="px-4 py-3">Purpose</th></tr></thead>
            <tbody>{ENDPOINTS.map(([m, p, d]) => (
              <tr key={p} className="border-t border-[#2A3447]"><td className="px-4 py-2.5 font-mono text-xs text-aether-300">{m}</td><td className="px-4 py-2.5 font-mono text-xs text-slate-200">{p}</td><td className="px-4 py-2.5 text-slate-400">{d}</td></tr>
            ))}</tbody>
          </table>
        </div>
        <div className="space-y-5 lg:col-span-5">
          <div className="panel p-5 text-sm text-slate-300">
            <p className="eyebrow mb-3">Authentication &amp; limits</p>
            <p>Send the session cookie, or <code className="font-mono text-amber-200">Authorization: Bearer &lt;access_token&gt;</code>. Uploads are limited to 120 requests/min per user; logins lock for 15 minutes after 5 failures. Third-party apps use OAuth2 (Authorization Code + PKCE): see the developer portal documentation.</p>
          </div>
          <TtsTerminalPanel />
        </div>
      </div>
    </div>
  );
}
